// A supplier's tax particulars on its page (#617): its PAN, shown in full only to a person whose Roles hold ID numbers
// and otherwise masked to its last four characters, its usual Nature of Payment and its lower-deduction certificate.
// The API checks each value (a PAN's form and the GSTIN on record that must carry it) and answers what is wrong in
// plain words. Bank details are not here: an Owner changes them in person, with a fresh code.
import { useEffect, useState, type FormEvent } from "react";
import type { Api, CounterpartyKind, MasterDataCorrections, TaxParticulars, TdsCertificate } from "../api.js";
import { ApiError } from "../http.js";
import { useWords } from "../i18n/words.js";

interface Certificate {
  number: string;
  rate: string;
  limit: string;
  from: string;
  to: string;
}

const NO_CERTIFICATE: Certificate = { number: "", rate: "", limit: "", from: "", to: "" };

const certificateOf = (c: TdsCertificate | null): Certificate =>
  c ? { number: c.number, rate: String(c.rate), limit: String(c.limit / 100), from: c.from, to: c.to } : NO_CERTIFICATE;

/** What the person changed, as the API takes a correction: an emptied value that was on record is cleared (null). */
export function taxChanges(
  read: TaxParticulars,
  pan: string,
  nature: string,
  certificate: Certificate,
  clearPan: boolean,
): MasterDataCorrections {
  const typed = Object.values(certificate).some((v) => v.trim() !== "");
  const given: TdsCertificate | undefined = typed
    ? {
        number: certificate.number.trim(),
        rate: Number(certificate.rate),
        limit: Math.round(Number(certificate.limit) * 100),
        from: certificate.from,
        to: certificate.to,
      }
    : undefined;
  const same = (a: TdsCertificate | null, b: TdsCertificate | undefined) =>
    JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  return {
    ...(pan.trim() !== "" ? { pan: pan.replace(/\s+/g, "").toUpperCase() } : clearPan && read.pan ? { pan: null } : {}),
    ...(nature !== (read.natureOfPayment ?? "") ? { natureOfPayment: nature === "" ? null : nature } : {}),
    ...(same(read.tdsCertificate, given) ? {} : { tdsCertificate: given ?? null }),
  };
}

export function TaxParticularsForm({
  api,
  counterparty,
}: {
  api: Pick<Api, "counterpartyTaxParticulars" | "saveCounterparty">;
  counterparty: { readonly id: string; readonly name: string; readonly kind: CounterpartyKind };
}) {
  const w = useWords();
  const [read, setRead] = useState<TaxParticulars | "failed">();
  const [said, setSaid] = useState<string>();
  const [version, setVersion] = useState(0);
  // Read again only for another Counterparty or once saved: the page passes a new `api` object as it renders.
  const { counterpartyTaxParticulars } = api;
  useEffect(() => {
    let current = true;
    counterpartyTaxParticulars(counterparty.id).then(
      (loaded) => {
        if (current) setRead(loaded);
      },
      () => {
        if (current) setRead("failed");
      },
    );
    return () => {
      current = false;
    };
  }, [counterpartyTaxParticulars, counterparty.id, version]);
  if (read === undefined) return <p className="muted">…</p>;
  if (read === "failed") return <p className="alert">{w.taxFailed}</p>;
  return (
    <>
      <Particulars
        key={version}
        read={read}
        onSave={async (changes) => {
          const saved = await api.saveCounterparty(
            counterparty.kind,
            { name: counterparty.name, ...changes },
            counterparty.id,
          );
          setSaid(saved.text);
          setVersion((v) => v + 1);
        }}
      />
      {said ? <p role="status">{said}</p> : null}
    </>
  );
}

function Particulars({
  read,
  onSave,
}: {
  read: TaxParticulars;
  onSave: (changes: MasterDataCorrections) => Promise<void>;
}) {
  const w = useWords();
  const [pan, setPan] = useState("");
  const [clearPan, setClearPan] = useState(false);
  const [nature, setNature] = useState(read.natureOfPayment ?? "");
  const [certificate, setCertificate] = useState(() => certificateOf(read.tdsCertificate));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();
  const editable = read.mayChange;
  const setCert = (key: keyof Certificate) => (event: { target: { value: string } }) =>
    setCertificate({ ...certificate, [key]: event.target.value });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    try {
      await onSave(taxChanges(read, pan, nature, certificate, clearPan));
    } catch (error) {
      setProblem(error instanceof ApiError && error.problems.length > 0 ? error.problems.join("; ") : w.masterFailed);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} aria-label={w.taxTitle}>
      <h3>{w.taxTitle}</h3>
      <p>
        {w.taxPan}: <span translate="no">{read.pan ?? w.taxPanNone}</span>
        {read.panMasked ? <span className="small muted"> {w.taxPanMasked}</span> : null}
      </p>
      {editable ? (
        <>
          <label className="field">
            {w.taxPanNew}{" "}
            <input value={pan} maxLength={12} autoComplete="off" onChange={(e) => setPan(e.target.value)} />
            <span className="small muted"> {w.taxPanHelp}</span>
          </label>
          {read.pan ? (
            <label className="field">
              <input type="checkbox" checked={clearPan} onChange={(e) => setClearPan(e.target.checked)} />{" "}
              {w.taxPanClear}
            </label>
          ) : null}
        </>
      ) : null}
      <label className="field">
        {w.taxNature}{" "}
        <select value={nature} disabled={!editable} onChange={(e) => setNature(e.target.value)}>
          <option value="">{w.taxNatureNone}</option>
          {read.natures.map((n) => (
            <option key={n.id} value={n.id}>
              {w.taxNatureWords(n.id, n.words)}
            </option>
          ))}
        </select>
      </label>
      <fieldset disabled={!editable}>
        <legend>{w.taxCertificate}</legend>
        <span className="small muted">{w.taxCertificateHelp}</span>
        <label className="field">
          {w.taxCertificateNumber} <input value={certificate.number} maxLength={50} onChange={setCert("number")} />
        </label>
        <label className="field">
          {w.taxCertificateRate}{" "}
          <input value={certificate.rate} maxLength={6} inputMode="decimal" onChange={setCert("rate")} />
        </label>
        <label className="field">
          {w.taxCertificateLimit}{" "}
          <input value={certificate.limit} maxLength={15} inputMode="decimal" onChange={setCert("limit")} />
        </label>
        <label className="field">
          {w.taxCertificateFrom} <input type="date" value={certificate.from} onChange={setCert("from")} />
        </label>
        <label className="field">
          {w.taxCertificateTo} <input type="date" value={certificate.to} onChange={setCert("to")} />
        </label>
      </fieldset>
      <p className="small muted">{w.taxBankDetails}</p>
      {editable ? (
        <button type="submit" className="btn" disabled={busy}>
          {w.taxSave}
        </button>
      ) : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
    </form>
  );
}
